import { Mail } from 'lucide-react'
import { useState } from 'react'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import { errorMessage } from '@/lib/api/errors'
import type { CustomerDetail } from '@/lib/api/types'
import { useSendOwnerEmailChange } from './api'

/**
 * When the owner asks support to change their sign-in email: send a link to their current
 * address. They choose the new address and confirm it from its inbox; nothing changes
 * until then. (Their team members ask the owner instead, from Members in the app.)
 */
export function OwnerEmailChange({ customer }: { customer: CustomerDetail }) {
  const send = useSendOwnerEmailChange(customer.id)
  const [confirming, setConfirming] = useState(false)
  const owner = customer.owner
  if (!owner || owner.account_closed || customer.closed_at) return null

  return (
    <div className="mt-4 grid gap-2 rounded-md border border-border px-4 py-3 text-[13px]">
      <p className="font-medium text-ink">Change the owner’s sign-in email</p>
      <p className="text-xs text-ink-muted">
        Sends a link to {owner.email}. They choose the new address and confirm it from that inbox; the email
        doesn’t change until they do. Only send it when the owner asked.
      </p>
      {confirming ? (
        <div className="flex flex-wrap items-center gap-2">
          <Button
            size="sm"
            loading={send.isPending}
            onClick={() =>
              send.mutate(undefined, {
                onSuccess: (r) => {
                  toast.success(r.message)
                  setConfirming(false)
                },
                onError: (e) => toast.error(errorMessage(e)),
              })
            }
          >
            Send to {owner.email}
          </Button>
          <Button size="sm" variant="ghost" onClick={() => setConfirming(false)}>
            Cancel
          </Button>
        </div>
      ) : (
        <Button size="sm" variant="secondary" className="w-fit" onClick={() => setConfirming(true)}>
          <Mail /> Send email change link
        </Button>
      )}
    </div>
  )
}
